import type React from 'react';
import { Link } from 'react-router-dom';

import { pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import { useAtomValue, useSetAtom } from 'jotai';

import BsLogoImage from '../../assets/images/bslogo.svg';
import deckIcon from '../../assets/images/deckIcon.svg';
import feedbackIcon from '../../assets/images/feedbackIcon.svg';
import homeIcon from '../../assets/images/homeIcon.svg';
import logoutIcon from '../../assets/images/logoutIcon.svg';
import menuIcon from '../../assets/images/menu.svg';
import performanceIcon from '../../assets/images/performanceIcon.svg';
import settingsIcon from '../../assets/images/settingsIcon.svg';
import tourIcon from '../../assets/images/tourIcon.svg';
import { useIsAssessmentCompleted } from '../../hooks/useIsAssessmentCompleted';
import type { User } from '../../model/domain/User';
import { sideMenuForceOpen } from '../../model/state/sideMenuState';
import { userState } from '../../model/state/userState';

import { SideMenuButton } from './SideMenuButton';

type SideMenuContentProps = {
  showMenu: boolean;
};

export const SideMenuContent: React.FC<SideMenuContentProps> = ({ showMenu }) => {
  const maybeUser = useAtomValue(userState);
  const user = O.toNullable(maybeUser);
  const libraryEnabled = user?.partner.enableLibrary;
  const performanceEnabled = user?.partner.enableUserPerformance;
  const isAssessmentCompleted = useIsAssessmentCompleted();
  const setForceShowMenu = useSetAtom(sideMenuForceOpen);

  const shownLibraryTour = pipe(
    maybeUser,
    O.map((u: User) => u.libraryTourShown),
    O.getOrElse<boolean>(() => false),
  );

  const upperMenuButtons: SideMenuButtonAttributes[] = [
    {
      icon: homeIcon,
      isDisabled: false,
      link: '/welcomeBack',
      name: "Today's Cards",
      paths: ['/study'],
    },
    {
      icon: performanceIcon,
      isDisabled: !isAssessmentCompleted,
      isInvisible: !performanceEnabled,
      link: '/performance',
      name: 'My Performance',
    },
    {
      icon: deckIcon,
      isDisabled: !isAssessmentCompleted,
      isInvisible: !libraryEnabled,
      link: shownLibraryTour ? '/library' : '/library_tour',
      name: 'Library',
    },
  ];

  interface SideMenuButtonAttributes {
    name: string;
    link: string;
    paths?: string[];
    isDisabled: boolean;
    isInvisible?: boolean;
    icon?: string;
  }

  const lowerMenuButtons: SideMenuButtonAttributes[] = [
    {
      icon: tourIcon,
      isDisabled: false,
      link: '/tour',
      name: 'Tour',
    },
    {
      icon: feedbackIcon,
      isDisabled: false,
      link: '/feedback',
      name: 'Feedback',
    },
    {
      icon: settingsIcon,
      isDisabled: false,
      link: '/settings',
      name: 'Settings',
      paths: ['/updatePws', '/preferences'],
    },
    // {
    //   name: 'FAQ',
    //   link: '/faq',
    //   isDisabled: false,
    // },
  ];

  const logoutMenuButton: SideMenuButtonAttributes = {
    icon: logoutIcon,
    isDisabled: false,
    link: '/logout',
    name: 'Logout',
  };

  return (
    <div className="flex h-screen flex-col overflow-y-hidden">
      <div className="flex items-center pb-2 pl-[18px] pt-[52px]">
        <button
          aria-label="Toggle menu"
          className="mr-2 block h-[22px] w-[22px] min-w-[22px] min-h-[22px]"
          onClick={() => {
            setForceShowMenu((old) => !old);
          }}
          type="button"
        >
          <img alt="menu" className="w-full h-full object-contain" src={menuIcon} />
        </button>

        {showMenu && (
          <Link className="pl-2.5" to="/welcomeBack">
            <img alt="Blank Slate" src={BsLogoImage} width={110} />
          </Link>
        )}
      </div>
      <div className="relative flex h-full flex-col justify-between">
        <div className="flex flex-col gap-[13px] px-[9px] pt-[58px]">
          {upperMenuButtons
            .filter((btn) => !btn.isInvisible)
            .map((btn) => {
              return <SideMenuButton key={btn.name} showMenu={showMenu} sideMenuButtonAttributes={btn} />;
            })}
        </div>
        <div className="flex flex-col gap-[7px] px-[9px]">
          {lowerMenuButtons.map((btn) => {
            return <SideMenuButton key={btn.name} showMenu={showMenu} sideMenuButtonAttributes={btn} />;
          })}
        </div>
        <div className="mb-[200px] mt-2.5 flex flex-col gap-[7px] px-[9px]">
          <SideMenuButton showMenu={showMenu} sideMenuButtonAttributes={logoutMenuButton} />
        </div>
      </div>
    </div>
  );
};
